import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Ban, Loader2, Mail, PenLine, Save } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  COMPANY,
  LANDLORD_TERM_LABEL,
  fillLetter,
  landlordAgreementBody,
  placeholdersIn,
  type LandlordTerm,
} from '@jt/domain';
import { api } from '../lib/api';
import { PageNav } from '../components/PageNav';
import { PrintContractButton, PrintableContract } from '../components/PrintContract';
import { DocumentEditor } from '../components/DocumentEditor';
import { SigningFields, SigningSequence } from '../components/SigningSequence';
import {
  GAP_ATTRIBUTE,
  InlineField,
  SigningBar,
  inlineFilling,
  type InlineFieldSpec,
} from '../components/InlineFields';
import { useToast } from '../components/Toast';
import { useSession } from '../lib/session';

/**
 * One building agreement, and the company's half of it.
 *
 * ## What signing does here that it does not do elsewhere
 *
 * It sends. Signing for the business issues the landlord's link and queues
 * their email in the same transaction as the signature, so there is no state
 * in which we have signed and they have not been told. That is worth saying on
 * the button, because "sign" and "sign and send" are different promises and
 * only one of them is true.
 */
export function LandlordAgreementPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();

  const agreement = useQuery({
    queryKey: ['landlord-agreement', id],
    queryFn: () => api.landlordAgreement(id),
    enabled: Boolean(id),
  });

  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [typedName, setTypedName] = useState('');
  const navigate = useNavigate();
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [correcting, setCorrecting] = useState<'recall' | 'amend' | null>(null);
  const [overriding, setOverriding] = useState(false);
  const [overrideDraft, setOverrideDraft] = useState<Record<string, unknown>>({});

  /*
   * Filled from the server once per agreement, then left alone. Watching the
   * query object would wipe whatever somebody is part-way through typing;
   * watching only the id fires before the data arrives and never runs again.
   */
  const hydrated = useRef<string | null>(null);
  useEffect(() => {
    if (!agreement.data || hydrated.current === id) return;
    hydrated.current = id;
    setDraft(agreement.data.values ?? {});
  }, [id, agreement.data]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['landlord-agreement', id] });
    await queryClient.invalidateQueries({ queryKey: ['landlord-agreements'] });
  };

  const save = useMutation({
    mutationFn: () => api.saveLandlordAgreementFields(id, draft),
    onSuccess: async () => {
      toast.done('Saved. Nothing is signed and nothing has been sent.');
      await invalidate();
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  const sign = useMutation({
    mutationFn: () =>
      api.signLandlordAgreementForCompany(id, {
        typedName,
        fields: draft,
      }),
    onSuccess: async (result) => {
      toast.done(`Signed. ${result.landlord.email} has been emailed a link to sign their side.`);
      setTypedName('');
      await invalidate();
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  /*
   * An administrator editing an agreement in whatever state it is in.
   *
   * Deliberately does not refuse. What it does instead is leave a trail: the
   * edit is audited under its own action name when signatures exist, and the
   * document's fingerprint stops matching them — which this page, the printed
   * copy and the landlord's own copy link all report. The record stops
   * claiming the signatures cover wording nobody signed.
   */
  const override = useMutation({
    mutationFn: () =>
      api.overrideLandlordAgreement(id, {
        fields: overrideDraft,
        reason: reason.trim() || undefined,
      }),
    onSuccess: async (updated) => {
      setOverriding(false);
      setReason('');
      toast.done(
        updated.integrity?.intact
          ? 'Saved.'
          : 'Saved. This agreement no longer matches the signatures on it, and now says so wherever it is read.',
      );
      await invalidate();
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  /*
   * Saving the clauses, by whichever route this agreement's state allows.
   *
   * While it is still ours, the ordinary save takes it — nothing is signed and
   * the landlord has never seen it. Once it is not, the same edit goes through
   * the admin override, which records it and lets the document report that it
   * no longer matches its signatures. One button either way: which of the two
   * is a fact about the agreement, not a decision for whoever is typing.
   */
  const saveWording = useMutation({
    mutationFn: (body: string) =>
      ourTurnNow
        ? api.saveLandlordAgreementFields(id, draft, { body })
        : api.overrideLandlordAgreement(id, { body, reason: 'Clauses edited' }),
    onSuccess: async (updated) => {
      toast.done(
        updated.integrity && !updated.integrity.intact
          ? 'Saved. This agreement no longer matches the signatures on it, and now says so wherever it is read.'
          : 'Saved. The agreement now reads as you have written it.',
      );
      await invalidate();
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  const cancel = useMutation({
    mutationFn: () => api.voidLandlordAgreement(id, reason),
    onSuccess: async () => {
      toast.done('Cancelled. The landlord’s link no longer works.');
      setVoiding(false);
      setReason('');
      await invalidate();
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  /*
   * Taking one back that the landlord has not signed.
   *
   * The terms become editable again and their link stops working, which is
   * the honest pairing: they were sent terms that are about to stop being the
   * terms, and a live link to them is a way to sign the wrong document.
   */
  const recall = useMutation({
    mutationFn: () => api.recallLandlordAgreement(id, reason),
    onSuccess: async () => {
      toast.done('Recalled. The terms are yours to edit again, and their old link is dead.');
      setCorrecting(null);
      setReason('');
      await invalidate();
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  /*
   * Amending a signed one, which navigates to the replacement rather than
   * staying here — the thing to work on now is the new draft, and leaving
   * somebody on the old version is how they edit the wrong document.
   */
  const amend = useMutation({
    mutationFn: () => api.amendLandlordAgreement(id, reason),
    onSuccess: async (replacement) => {
      toast.done(`${replacement.reference} raised. The signed one stays exactly as it was.`);
      setCorrecting(null);
      setReason('');
      await invalidate();
      navigate(`/landlord-agreements/${replacement.id}`);
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  if (agreement.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  if (agreement.isError || !agreement.data) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <h2 className="font-heading text-xl">No such agreement</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          It may have been cancelled, or replaced by a newer version.
        </p>
      </div>
    );
  }

  const it = agreement.data;
  const signed = it.signatures.map((one) => one.party);
  const closed = it.status === 'VOIDED' || it.status === 'SUPERSEDED';
  const ourTurn = it.turn === 'company' && !closed;
  const canSign = can('landlord_agreement:sign_company');
  /*
   * Preparing the terms is `manage` — the same capability the API's "save the
   * company's half, without signing" route requires. Signing is separate and
   * admin-only, which is the restriction that was always meant.
   */
  const canFill = can('landlord_agreement:manage');

  /*
   * `amend` is admin-only, and it is what unlocks editing an agreement whose
   * turn has passed — including a signed one. The ordinary form above stops at
   * the company's turn, which is still the right default.
   */
  const canOverride = can('landlord_agreement:amend');

  /*
   * Whose turn it is decides which route saves; what the person holds decides
   * whether they see the control at all. While it is ours, anybody who can
   * manage agreements may rewrite the clauses. After that it takes an
   * administrator, because the edit is then recorded against signatures.
   */
  const ourTurnNow = it.turn === 'company' && !closed;

  /*
   * ── Our half, filled in where it is asked for ───────────────────────────
   *
   * The same move as the landlord's own page: the boxes go into the clauses.
   * Schedule A asks where the fibre enters a building, and somebody answering
   * that should be looking at the schedule rather than at a field labelled
   * "Entry point" two screens below it.
   *
   * Gated on it actually being our turn *and* the person being allowed to
   * fill: an agreement that has gone to the landlord must read as a document,
   * not as a form somebody could still type into.
   */
  const SIGNATURE_KEY = '__typedName';
  const fillingNow = ourTurn && canFill;

  const mine: InlineFieldSpec[] = it.fields
    .filter((field) => field.party === 'company')
    .map((field) => ({
      key: field.key,
      label: field.label,
      kind: field.kind,
      required: field.required,
      hint: field.hint,
      options: field.options,
    }));

  /*
   * Split by what the wording actually contains.
   *
   * Most fields are gaps in the text and are typed into the text. A few have
   * nowhere to go: "Installation fee" is a choice whose *result* is the
   * sentence `{{feeDescription}}`, so there is no `{{feeBasis}}` anywhere to
   * put a dropdown in. Six of this agreement's fields are like that, and the
   * first version of this screen dropped the form outright — which left no way
   * at all to set the fee, the term, or the exclusivity grant.
   *
   * Asked of the body rather than listed here, because the body is editable:
   * a clause rewritten to mention a field directly gains an inline gap for it
   * with nothing to change on this page.
   */
  const gaps = placeholdersIn(it.body);
  const inDocument = mine.filter((field) => gaps.has(field.key));
  const offDocument = it.fields.filter(
    (field) => field.party === 'company' && !gaps.has(field.key),
  );

  const barFields: InlineFieldSpec[] = fillingNow
    ? canSign
      ? [...mine, { key: SIGNATURE_KEY, label: 'Your signature', kind: 'signature', required: true }]
      : mine
    : [];

  const filling = inlineFilling(barFields, it.values, { ...draft, [SIGNATURE_KEY]: typedName });

  const slots = fillingNow
    ? Object.fromEntries(
        inDocument.map((field) => [
          field.key,
          <InlineField
            key={field.key}
            field={field}
            value={filling.valueOf(field.key)}
            onChange={(value) => setDraft((old) => ({ ...old, [field.key]: value }))}
          />,
        ]),
      )
    : undefined;

  const leaveOpen = fillingNow
    ? new Set(inDocument.map((field) => field.key))
    : undefined;
  const mayEditWording = (ourTurnNow && canFill) || (!ourTurnNow && canOverride && !closed);
  const startOverride = () => {
    setOverrideDraft({ ...(it.values as Record<string, unknown>) });
    setReason('');
    setOverriding(true);
  };
  const canManage = can('landlord_agreement:manage');
  const canAmend = can('landlord_agreement:amend');

  /*
   * Which correction applies is the agreement's state, not the person's.
   *
   * Waiting on the landlord: recall it, which makes the terms editable again.
   * Signed: amend it, which raises version N+1 and leaves the signed one
   * exactly as it is. A signed agreement is never edited in place — the
   * landlord holds a copy saying neither side can change it.
   */
  const mayRecall = canAmend && it.status === 'AWAITING_LANDLORD';
  const mayAmend = canAmend && it.status === 'SIGNED';

  const date = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      : null;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageNav
        crumbs={[
          { label: 'Building access', to: '/landlord-agreements' },
          { label: it.reference },
        ]}
      />

      <div className="flex flex-wrap items-start justify-between gap-3 print:hidden">
        <div>
          <h1 className="font-heading text-2xl">{it.propertyName}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {it.reference} · {it.landlord.name} · {LANDLORD_TERM_LABEL[it.term as LandlordTerm]}
            {date(it.startDate) ? ` from ${date(it.startDate)}` : ''}
            {date(it.endDate) ? ` to ${date(it.endDate)}` : ''}
          </p>
        </div>
        <PrintContractButton />
      </div>

      {closed && (
        <p className="card border-accent-300 bg-accent-50 text-sm text-accent-900">
          This agreement was cancelled{it.voidReason ? ` — ${it.voidReason}` : ''}. The landlord’s
          link no longer works.
        </p>
      )}

      {/* ── Who signs, and in what order ──────────────────────────────── */}
      <section className="card space-y-3 print:hidden">
        <h2 className="font-heading text-lg">Signing order</h2>
        <SigningSequence
          signed={signed}
          turn={closed ? null : it.turn}
          counterparty="landlord"
          signatures={it.signatures}
        />

        {!closed && it.next && (
          <p className="flex items-center gap-2 rounded-xl bg-muted/40 p-3 text-sm">
            <ArrowRight className="h-4 w-4 shrink-0 text-primary-600" aria-hidden="true" />
            <span>
              Now with <strong>{it.next.label}</strong>
              {ourTurn
                ? ' — that is us.'
                : `. It was emailed to ${it.landlord.email}${
                    date(it.sentAt) ? ` on ${date(it.sentAt)}` : ''
                  }.`}
            </span>
          </p>
        )}
      </section>

      {/*
        ── The document, and where it is edited ──────────────────────────

        The edit control is on the document rather than in a form underneath
        it. It was a small link inside the terms section, below two dozen
        fields, and the result was a feature that worked and that nobody could
        find: you open an agreement, you are looking at the clauses, and the
        place you expect to change them is the thing you are looking at.
      */}
      <DocumentEditor
        heading="The agreement"
        blurb="All twenty-five clauses and both schedules. The gaps fill in as they are typed, so what is read is what is signed."
        standard={landlordAgreementBody()}
        body={it.body}
        values={{ ...it.values, ...draft }}
        canEdit={mayEditWording}
        saving={saveWording.isPending}
        onSave={(body) => saveWording.mutateAsync(body)}
        slots={slots}
        leaveOpen={leaveOpen}
        note={
          it.status === 'SIGNED'
            ? 'Both sides have signed this. Changing the clauses is recorded, and the agreement will say it no longer matches its signatures — here, on the printed copy, and on the copy the landlord opens. Their emailed copy does not change.'
            : it.status === 'AWAITING_LANDLORD'
              ? 'The landlord has this and has not signed yet. What you save here is what they will see when they open their link.'
              : undefined
        }
      />

      {/*
        ── Our half ──────────────────────────────────────────────────────

        Filling it in and signing it are two different permissions, and this
        screen used to treat them as one: the whole section was behind
        `landlord_agreement:sign_company`, which is **admin only**. So a
        salesperson who had raised the agreement — and who the API happily
        accepts field edits from, through "save the company's half, without
        signing" — was shown no form at all. From their side the agreement was
        simply not editable, which is exactly what it looked like.

        Now the form belongs to `landlord_agreement:manage` and only the
        signature block is reserved. Someone who can commit the business still
        has to sign, which was the point of the restriction; preparing the
        terms never needed to be.
      */}
      {ourTurn && canFill && (
        <section className="card space-y-4 print:hidden">
          <div>
            <h2 className="font-heading text-lg">
              {canSign ? 'The terms, and our signature' : 'The terms'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {canSign
                ? 'Type the terms into the agreement above — every gap is highlighted. Everything required has to be filled before this can be signed, and signing emails it to the landlord.'
                : 'Type the terms into the agreement above — every gap is highlighted. Everything required has to be filled before an administrator can sign it, and signing is what emails it to the landlord.'}
            </p>
          </div>

          {/*
            * The few answers the agreement has no gap for.
            *
            * The fee, the term and the exclusivity grant are choices whose
            * *result* is a sentence in the document, so there is no blank in
            * the text to put them in. Everything else is up there, in the
            * clause that asks for it.
            */}
          {offDocument.length > 0 && (
            <SigningFields
              fields={offDocument}
              values={{ ...it.values, ...draft }}
              party="company"
              onChange={(key, value) => setDraft((old) => ({ ...old, [key]: value }))}
            />
          )}

          {it.outstanding.company && (
            <p className="rounded-xl bg-accent-50 p-3 text-sm text-accent-900">
              {it.outstanding.company}
            </p>
          )}

          {canSign && (
          <label className="block border-t border-border pt-4 text-sm">
            Type your full name to sign
            <input
              {...{ [GAP_ATTRIBUTE]: SIGNATURE_KEY }}
              className="input signature-ink mt-1 w-full text-2xl"
              value={typedName}
              onChange={(event) => setTypedName(event.target.value)}
              placeholder={String(it.values.companySignatoryName ?? 'Signing for the company')}
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              It has to match the name on the agreement. Typing it is your signature, and the time,
              the address you signed from and a fingerprint of this exact wording are kept with it.
            </span>
          </label>
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
            {canSign && (
            <button
              type="button"
              className="btn-primary"
              disabled={typedName.trim().length < 3 || sign.isPending}
              onClick={() => sign.mutate()}
            >
              {sign.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Mail className="h-4 w-4" aria-hidden="true" />
              )}
              Sign and email the landlord
            </button>
            )}
            <button
              type="button"
              className={canSign ? 'btn-secondary' : 'btn-primary'}
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="h-4 w-4" aria-hidden="true" />
              )}
              {canSign ? 'Save and finish later' : 'Save the terms'}
            </button>
          </div>

          {!canSign && (
            <p className="rounded-xl bg-muted/40 p-3 text-sm text-muted-foreground">
              You can prepare these terms but not sign them. Signing commits the business to
              leaving equipment in somebody&rsquo;s building for years, so an administrator does
              it — and that is also what emails the agreement to the landlord. Nothing has reached
              them yet.
            </p>
          )}
        </section>
      )}

      {/*
        * What is left, and the pen.
        *
        * Sticky, because the gaps are now spread through twenty-five clauses
        * and two schedules. Without it, "have I filled everything in?" is
        * answered by scrolling the whole agreement again.
        */}
      {fillingNow && (
        <SigningBar
          outstanding={filling.outstanding}
          total={barFields.length}
          onJump={filling.jumpTo}
        >
          <button
            type="button"
            className="btn-secondary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            Save
          </button>
          {canSign && (
            <button
              type="button"
              className="btn-primary"
              disabled={typedName.trim().length < 3 || sign.isPending}
              onClick={() => sign.mutate()}
            >
              {sign.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Mail className="h-4 w-4" aria-hidden="true" />
              )}
              Sign and email
            </button>
          )}
        </SigningBar>
      )}

      {/* Nothing to fill and nothing to sign: say who it is waiting on. */}
      {ourTurn && !canFill && (
        <section className="card print:hidden">
          <h2 className="font-heading text-lg">Waiting for a signature</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            The terms are settled and the landlord has not been contacted. Somebody who can commit
            the business has to sign it — ask an administrator.
          </p>
        </section>
      )}

      {/* ── What the landlord has filled in ───────────────────────────── */}
      {it.turn !== 'company' && (
        <TheirAnswers fields={it.fields} values={it.values} />
      )}

      {/*
        ── Changed since it was signed ───────────────────────────────────

        Shown before anything else, because it changes what everything below
        means. The landlord holds the version they signed; this one no longer
        matches it.
      */}
      {it.integrity && !it.integrity.intact && (
        <section className="card border-danger-300 bg-danger-50 print:hidden">
          <h2 className="flex items-center gap-2 font-heading text-lg text-danger-800">
            <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
            Changed after it was signed
          </h2>
          <p className="mt-1 text-sm text-danger-900">{it.integrity.detail}</p>
          <p className="mt-2 text-sm text-danger-900">
            The audit trail records who changed it and when. If this needs to be the agreement
            both sides are held to, raise an amendment and have it signed again.
          </p>
        </section>
      )}

      {/* ── Correcting one that has left the drafting stage ───────────── */}
      {(mayRecall || mayAmend) && (
        <section className="card space-y-3 print:hidden">
          <div>
            <h2 className="font-heading text-lg">
              {mayRecall ? 'Change these terms' : 'Replace this agreement'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {mayRecall ? (
                <>
                  The landlord has not signed yet. Recalling takes our signature back so the terms
                  can be edited and signed again — <strong>their current link stops working</strong>,
                  because it opens terms that are about to stop being the terms. A new link goes out
                  when it is signed again.
                </>
              ) : (
                <>
                  A signed agreement is never edited. The landlord holds a copy saying neither side
                  can change it, so this raises <strong>version {it.version + 1}</strong> as a fresh
                  draft carrying these terms over. Both sides sign it again, and the signed one
                  stays exactly as it is and readable — it is marked superseded only when the
                  replacement is signed.
                </>
              )}
            </p>
          </div>

          {correcting ? (
            <div className="space-y-3 border-t border-border pt-3">
              <label className="block text-sm">
                {correcting === 'recall' ? 'Why is it being recalled?' : 'Why is it being replaced?'}
                <input
                  className="input mt-1 w-full"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={
                    correcting === 'recall'
                      ? 'Exclusivity was agreed after this went out'
                      : 'The landlord asked for exclusivity for the whole term'
                  }
                />
                <span className="mt-1 block text-xs text-muted-foreground">
                  Kept on the audit trail against this agreement.
                </span>
              </label>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={
                    reason.trim().length < 3 || recall.isPending || amend.isPending
                  }
                  onClick={() => (correcting === 'recall' ? recall.mutate() : amend.mutate())}
                >
                  {(recall.isPending || amend.isPending) && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  {correcting === 'recall' ? 'Recall it' : 'Raise the replacement'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setCorrecting(null);
                    setReason('');
                  }}
                >
                  Leave it
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCorrecting(mayRecall ? 'recall' : 'amend')}
            >
              <PenLine className="h-4 w-4" aria-hidden="true" />
              {mayRecall ? 'Recall and edit the terms' : 'Raise an amendment'}
            </button>
          )}
        </section>
      )}

      {/*
        ── Editing it anyway ─────────────────────────────────────────────

        For an agreement whose turn has passed — one with the landlord, or one
        already signed. The ordinary form higher up covers the rest.
      */}
      {canOverride && !ourTurn && !closed && (
        <section className="card space-y-3 print:hidden">
          <div>
            <h2 className="font-heading text-lg">Edit it anyway</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {it.status === 'SIGNED' ? (
                <>
                  This is signed by both sides. You can still change it, and the change is
                  recorded: the agreement will say it no longer matches its signatures, here and
                  on the copy the landlord opens. <strong>Their emailed copy does not change.</strong>{' '}
                  Raising an amendment is the version both sides end up held to — this is for
                  correcting the record.
                </>
              ) : (
                <>
                  The landlord has this and has not signed yet. Editing changes what they will see
                  when they open their link. Recalling it above is the tidier route — it takes our
                  signature back and sends a fresh link.
                </>
              )}
            </p>
          </div>

          {overriding ? (
            <div className="space-y-3 border-t border-border pt-3">
              <SigningFields
                fields={it.fields}
                values={overrideDraft}
                party="company"
                onChange={(key, value) => setOverrideDraft((old) => ({ ...old, [key]: value }))}
              />

              <label className="block border-t border-border pt-3 text-sm">
                Why is it being changed?
                <input
                  className="input mt-1 w-full"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Exclusivity was agreed verbally and never recorded"
                />
                <span className="mt-1 block text-xs text-muted-foreground">
                  Kept on the audit trail beside exactly which fields changed.
                </span>
              </label>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={override.isPending}
                  onClick={() => override.mutate()}
                >
                  {override.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  Save the change
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setOverriding(false);
                    setReason('');
                  }}
                >
                  Leave it
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="btn-secondary" onClick={startOverride}>
              <PenLine className="h-4 w-4" aria-hidden="true" />
              Edit the terms
            </button>
          )}
        </section>
      )}

      {/* ── Cancelling ────────────────────────────────────────────────── */}
      {canManage && !closed && it.status !== 'SIGNED' && (
        <section className="print:hidden">
          {voiding ? (
            <div className="card space-y-3">
              <label className="block text-sm">
                Why is it being cancelled?
                <input
                  className="input mt-1 w-full"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="The landlord asked for different cable routes"
                />
                <span className="mt-1 block text-xs text-muted-foreground">
                  Kept with the agreement, and the landlord’s link stops working immediately.
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-danger"
                  disabled={reason.trim().length < 3 || cancel.isPending}
                  onClick={() => cancel.mutate()}
                >
                  {cancel.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  Cancel this agreement
                </button>
                <button type="button" className="btn-secondary" onClick={() => setVoiding(false)}>
                  Leave it
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-accent-700 hover:underline"
              onClick={() => setVoiding(true)}
            >
              <Ban className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
              Cancel this agreement
            </button>
          )}
        </section>
      )}

      {/*
        The copy that comes out of a printer. `PrintableContract` carries the
        letterhead and the watermark; this is the copy a landlord keeps, so it
        is the one that has to be recognisably ours.
      */}
      <PrintableContract
        title={it.title}
        reference={it.reference}
        company="GoSpeedNet"
        body={fillLetter(it.body, it.values)}
        issuedOn={it.sentAt}
        standing={it.status === 'SIGNED' ? 'Executed by both parties' : null}
        parties={[
          { role: 'The provider', name: COMPANY.legalName },
          {
            role: 'The landlord / property owner',
            name: it.landlord.name,
            lines: [it.propertyName, it.landlord.phone, it.landlord.email],
          },
        ]}
        counterpartyLabel="The landlord / property owner"
        signatures={it.signatures.map((one) => ({
          party: one.party,
          name: one.signerName,
          title: one.signerTitle,
          signedAt: one.signedAt,
        }))}
      />
    </div>
  );
}

/**
 * What the landlord filled in, shown and not editable.
 *
 * Hiding it would be easier and worse: whoever chases the agreement needs to
 * see the ID number and the caretaker's phone that came back. What they must
 * not be able to do is type in the landlord's boxes — a different thing from
 * seeing them, and something the API refuses regardless by dropping any key
 * that does not belong to the party sending it.
 */
function TheirAnswers({
  fields,
  values,
}: {
  fields: Array<{ key: string; label: string; party: string; kind: string }>;
  values: Record<string, unknown>;
}) {
  const theirs = fields.filter((field) => field.party === 'counterparty');
  const filled = theirs.filter((field) => {
    const value = values[field.key];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });

  if (filled.length === 0) {
    return (
      <section className="card print:hidden">
        <h2 className="font-heading text-lg">The landlord’s half</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Nothing filled in yet. They have been emailed a link.
        </p>
      </section>
    );
  }

  return (
    <section className="card print:hidden">
      <h2 className="font-heading text-lg">Filled in by the landlord</h2>
      <dl className="mt-2 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {filled.map((field) => (
          <div key={field.key} className="flex flex-wrap items-baseline justify-between gap-2">
            <dt className="text-sm text-muted-foreground">{field.label}</dt>
            <dd
              className={`text-sm font-medium ${
                field.kind === 'signature' ? 'signature-ink text-lg' : ''
              }`}
            >
              {String(values[field.key])}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
